<template>
  <section class="section get-details">
    <div class="get-details__group">
      <div class="get-details__form-wrapper">
        <h2>
          {{ $t("Join", { brand: $t(brand) }) }}
        </h2>
        <div class="get-details__form-container">
          <SelectTab
            v-if="isSelectTab"
            :options="cooperationOptions"
            :value="selectedCooperationType"
            @select="selectCooperationType"
          >
            <template #option="slotProps">
              {{ $t(slotProps.option.value) }}
            </template>
          </SelectTab>
          <form class="get-details__form" @submit.prevent="onSubmit">
            <BaseInput
              v-model="state.name"
              :placeholder="$t('Name')"
              :inner-placeholder="$t('Your name')"
              :error="errors?.name?.[0]"
            />
            <BaseInput
              v-model="state.phone"
              label="phone"
              :placeholder="$t('Phone')"
              :inner-placeholder="phoneNumberFormat[countryCode].placeholder"
              :error="errors?.phone?.[0]"
              :validator="validators.phone.phoneValidator(countryCode)"
              :unavailable-symbols="validators.phone.unavailableSymbols"
              @is-error="validationErrors['phone']"
              type="tel"
              :help-text="$t('contacts.formatNumber')"
              :trim="true"
            />
            <BaseInput
              v-model="state.email"
              label="email"
              :placeholder="$t('Email')"
              :inner-placeholder="emailPlaceholder"
              :error="errors?.email?.[0]"
              :validator="validators.email"
              @is-error="validationErrors['email']"
            />
            <BaseInput
              v-model="state.companyName"
              :placeholder="preparedCompanyInfoTitle.placeholder"
              :inner-placeholder="preparedCompanyInfoTitle.innerPlaceholder"
              :error="errors?.companyName?.[0]"
            />
            <AgreementCheckbox
              v-model="state.processPersonalData"
              class="get-details__agreement"
            />
            <RecaptchaV2Fallback v-if="showV2Captcha" @verify="onV2Verify" />
            <BaseButton
              type="submit"
              :disabled="isDisabledButton || showV2Captcha"
              class="get-details__button"
            >
              {{ $t("Get an Offer") }}
            </BaseButton>
          </form>
        </div>
      </div>

      <div class="get-details__image">
        <img
          src="/image/get-details-section-image.webp"
          alt="pic"
          decoding="async"
          loading="lazy"
        />
      </div>
    </div>
  </section>
</template>
<script lang="ts">
import { storeToRefs } from "pinia";
import SelectTab from "../input/SelectTab/SelectTab.vue";
import useInputValidation from "~/composables/useInputValidation";
import useWindowResize from "~/composables/useWindowResize";
import { useConfigStore } from "~/stores/config";
import { CooperationTypes, SuccessModalTypes } from "~/types/entities";
import { phoneNumberFormat } from "~/utils/validators/phone";
import AgreementCheckbox from "~/components/agreementCheckbox/AgreementCheckbox.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import BaseInput from "~/components/input/BaseInput.vue";
import BaseCheckBox from "~/components/input/BaseCheckbox.vue";
import validators from "~/utils/validators";
import { submitRecaptcha } from "~/api/formRequests";
import removePlusSymbol from "~/utils/removePlusSymbol";
import { successModalKey } from "~/symbols";
import isEqual from "lodash/isEqual";
import { hidePD } from "~/utils/analytics";
import { useReCaptcha } from "vue-recaptcha-v3";
import RecaptchaV2Fallback from "~/components/recaptcha/V2Fallback.vue";
import getEmailPlaceHolder from "~/utils/getEmailPlaceholder.js";

export const COOPERATION_OPTIONS_DEFAULT = [
  { value: "For Companies", id: CooperationTypes.CONNECT_COMPANY },
  { value: "For Partners", id: CooperationTypes.PARTNER },
];

export const COOPERATION_OPTIONS_TABLET = [
  { value: "Connect the company", id: CooperationTypes.CONNECT_COMPANY },
  { value: "Become a Partner", id: CooperationTypes.PARTNER },
];

export default defineNuxtComponent({
  components: {
    AgreementCheckbox,
    BaseCheckBox,
    BaseInput,
    BaseButton,
    RecaptchaV2Fallback,
    SelectTab,
  },
  props: {
    isSelectTab: {
      type: Boolean,
      default: true,
    },
    cooperationType: {
      type: String,
      default: CooperationTypes.CONNECT_COMPANY,
    },
  },
  setup(props) {
    const { gtag } = useGtag();
    const { t } = useI18n();

    const { isMobileScreen } = useWindowResize();
    const { countryCode } = storeToRefs(useConfigStore());
    const { brand } = useRuntimeConfig().public;
    const defaultState = {
      companyName: "",
      name: "",
      email: "",
      phone: "",
      location: "default", // Remove this when backend will be changed to not receive the location
      processPersonalData: false,
      country: countryCode.value,
    };
    const state = reactive({ ...defaultState });

    let showSuccessModal = (_type: SuccessModalTypes): void => {};

    const errors = ref<any>(null);
    const isSentForm = ref(false);
    const showV2Captcha = ref(false);
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

    const { isValid, validationErrors } = useInputValidation(state, { optionalFields: ["phone"] });
    const isDisabledButton = computed(() => !isValid.value || !!errors.value);
    const loading = ref(false);

    const selectedCooperationType = ref(props.cooperationType);

    const preparedCompanyInfoTitle = computed(() => {
      const isPartner = selectedCooperationType.value === CooperationTypes.PARTNER;

      const titlePlaceholder = isPartner ? t("Facility name") : t("Company name");
      const innerPlaceholder = isPartner ? t("Facility name") : t("Company");

      return {
        placeholder: titlePlaceholder,
        innerPlaceholder: innerPlaceholder
      };
    });

    const gEventPayload = computed(() =>
      isEqual(state, defaultState) ? undefined : hidePD(state)
    );

    const cooperationOptions = computed(() =>
      isMobileScreen.value
        ? COOPERATION_OPTIONS_DEFAULT
        : COOPERATION_OPTIONS_TABLET
    );

    const preparedPhone = computed(() => removePlusSymbol(state.phone));

    watch(
      () => state,
      () => {
        if (errors.value) {
          errors.value = null;
        }
      },
      { deep: true }
    );

    async function onSubmit() {
      gtag("event", GetDetailsEvents.submit, gEventPayload.value);

      loading.value = true;
      try {
        const token = await getV3Token();
        const { success } = await submitRecaptcha({
          form: {
            ...state,
            phone: preparedPhone.value,
          },
          token,
          formType: selectedCooperationType.value,
        });

        if (!success) {
          showV2Captcha.value = true;
          return;
        }

        handleSuccessfulSubmit();
      } catch (error: any) {
        handleSubmitError(error);
      } finally {
        loading.value = false;
      }
    }

    async function onV2Verify(v2Token: string) {
      loading.value = true;
      try {
        const { success } = await submitRecaptcha({
          form: {
            ...state,
            phone: preparedPhone.value,
          },
          v2Token,
          formType: selectedCooperationType.value,
        });

        if (!success) {
          throw new Error("reCAPTCHA verification failed");
        }

        handleSuccessfulSubmit();
      } catch (error: any) {
        handleSubmitError(error);
      } finally {
        loading.value = false;
      }
    }

    function handleSuccessfulSubmit() {
      if (CooperationTypes.CONNECT_COMPANY === selectedCooperationType.value) {
        showSuccessModal(SuccessModalTypes.CONNECT_COMPANY);
      } else {
        showSuccessModal(SuccessModalTypes.PARTNER);
      }
      resetState();
    }

    function handleSubmitError(error: any) {
      if (error?.data?.errors) {
        errors.value = error.data.errors;
      }
      resetState();
      showSuccessModal(error?.data?.modalType || SuccessModalTypes.ISSUE);
    }

    const emailPlaceholder = getEmailPlaceHolder(brand);

    function selectCooperationType(optionId: CooperationTypes) {
      gtag("event", GetDetailsEvents[optionId], gEventPayload.value);
      selectedCooperationType.value = optionId;
    }

    function resetState() {
      Object.assign(state, defaultState);
      showV2Captcha.value = false;
    }

    async function getV3Token() {
      await recaptchaLoaded();
      const token = await executeRecaptcha("submit");
      if (!token) throw new Error("reCAPTCHA token missing");
      return token;
    }

    onMounted(() => {
      showSuccessModal = inject(successModalKey, () => {});
    });

    return {
      state,
      loading,
      isSentForm,
      isDisabledButton,
      countryCode,
      emailPlaceholder,
      brand,
      errors,
      validationErrors,
      onSubmit,
      onV2Verify,
      showV2Captcha,
      validators,
      phoneNumberFormat,
      cooperationOptions,
      selectCooperationType,
      selectedCooperationType,
      preparedCompanyInfoTitle,
    };
  },
});
</script>
